import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/lib/firebase';

type AuthState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | { status: 'authenticated'; role: string | null; tenantId: string | null };

// Role-gated guard. Reads the Firebase custom-claim `role` (set by the
// admin during user provisioning) and rejects users whose role isn't in
// `allow`. Falls back to /login when unauthenticated and to /dashboard
// (the safe customer landing) when authenticated-but-wrong-role.
//
// Allowed roles in cleanyarn (from web/app/login/page.tsx):
//   - operations_manager, system_admin    -> /admin
//   - warehouse_operations                -> /warehouse
//   - facility_director, finance_officer,
//     facility_staff, tenant_admin        -> /dashboard (customer)
//   - field_staff                         -> mobile only (rejected here)
export default function RequireRole({
  allow,
  children,
}: {
  allow: readonly string[];
  children: ReactNode;
}) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });
  const location = useLocation();

  useEffect(() => {
    return onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setState({ status: 'unauthenticated' });
        return;
      }
      try {
        const token = await user.getIdTokenResult();
        setState({
          status: 'authenticated',
          role: (token.claims.role as string | undefined) ?? null,
          tenantId: (token.claims.tenant_id as string | undefined) ?? null,
        });
      } catch {
        // Token fetch failure -> treat as unauthenticated so the user goes
        // back through /login and we get a fresh token.
        setState({ status: 'unauthenticated' });
      }
    });
  }, []);

  if (state.status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center text-sage-700">
        Checking your session…
      </div>
    );
  }

  if (state.status === 'unauthenticated') {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!state.role || !allow.includes(state.role)) {
    // Authenticated but not authorised. Send the user to *their* natural
    // home (matching the post-login redirect logic in pages/login/index.tsx)
    // rather than back to /login — they're already signed in.
    return <Navigate to={homeForRole(state.role, state.tenantId)} replace />;
  }

  return <>{children}</>;
}

// Role constants used by routes.tsx — keeping them here so the source of
// truth lives next to the guard.
export const ADMIN_ROLES = ['operations_manager', 'system_admin'] as const;
export const WAREHOUSE_ROLES = ['warehouse_operations'] as const;
export const CUSTOMER_ROLES = [
  'facility_director',
  'finance_officer',
  'facility_staff',
  'tenant_admin',
] as const;

// Resolve where an authenticated user should land based on their custom
// claims. Mirrors pages/login/index.tsx's role-based post-login redirect.
// Exported because pages/login/index.tsx's "already-authed" effect uses it.
export function homeForRole(role: string | null, tenantId: string | null): string {
  if (role && (ADMIN_ROLES as readonly string[]).includes(role)) return '/admin';
  if (role && (WAREHOUSE_ROLES as readonly string[]).includes(role)) return '/warehouse';
  if (tenantId) return '/dashboard';
  return '/login';
}
